// Internal modules
import { appStore } from '@/store';
import { $logger } from '../logger';
import { features } from '../features';
import { getAccessToken, getToken } from '../globals';

/**
 * @function dropLoginCookie
 * @returns {Promise}
 * @description drops a login cookie and resolves the promise with
 * `true` if it was a success or `false` if it wasn't
 */
const dropLoginCookie = data => {
  const { type: tokenType, value: tokenValue } = getAccessToken();
  const { flags } = data || {};

  if (!tokenValue || (tokenType !== 'signup' && !flags?.is3DSecureRequired)) {
    $logger.warn('abort_auth_cookie_drop', {
      tokenPresent: Boolean(tokenValue),
      tokenType,
    });
    return Promise.resolve(false);
  }

  const options = {
    credentials: 'same-origin',
    headers: {
      'x-paypal-internal-euat': tokenValue,
    },
    method: 'GET',
  };

  return fetch('/checkoutweb/drop', options)
    .then(() => {
      $logger.info('auth_cookie_dropped_successfully');
      return true;
    })
    .catch(err => {
      $logger.error('auth_cookie_drop_failed', {
        error: err.message,
      });
      return false;
    });
};

const conditionallyDropLoginCookie = valueToReturn => {
  const { isUserLoggedIn } = appStore.getState().user;
  const isSignUp = getAccessToken().type === 'signup';

  const { flags } = valueToReturn || {};

  if ((!flags?.is3DSecureRequired && !isSignUp) || isUserLoggedIn) {
    return Promise.resolve(valueToReturn);
  }

  const { user: userActions } = appStore.getActions();

  // Opt into one touch if POMA flow
  if (features.isActive('isPOMAEligible')) {
    const token = getToken();

    // Lazy loading function since it's only used in the POMA flow
    // prettier-ignore
    return import('@/components/PasswordlessOnboarding/utils')
      .then(({ dropOneTouchCookie }) => dropOneTouchCookie(token))
      .then(() => userActions.setUserLoggedIn())
      .catch(error => {
        $logger.error('drop_one_touch_cookie_error', {
          message: error?.message || error,
        });
      })
      // Continue with flow even if one touch activation fails
      .then(() => valueToReturn);
  }

  // Drop login cookie for non-POMA signup
  return dropLoginCookie(valueToReturn).then(isUserLoggedIn => {
    isUserLoggedIn && userActions.setUserLoggedIn();
    return valueToReturn;
  });
};

export { conditionallyDropLoginCookie };
